import { useEffect, useState } from "react";
import { detectSelectedFont, loadFontData, type FontOption } from "../services/fontData";
import styles from "./InlinePanel.module.css";

export type TextPanelMode = "replaceFont" | "connectorWeight";

interface TextConfigPanelProps {
  readonly mode: TextPanelMode;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onApplyFont: (fromFont: string, toFont: string, allSlides: boolean) => void;
  readonly onApplyWeight: (weight: number) => void;
}


export function TextConfigPanel({
  mode,
  busy,
  onCancel,
  onApplyFont,
  onApplyWeight,
}: TextConfigPanelProps): React.ReactElement {
  const isFont = mode === "replaceFont";
  const [fromFont, setFromFont] = useState("");
  const [toFont, setToFont] = useState("");
  const [scope, setScope] = useState<"slide" | "all">("slide");
  const [fonts, setFonts] = useState<FontOption[]>([]);
  const [weight, setWeight] = useState(1.5);

  useEffect(() => {
    if (!isFont) return;
    let live = true;
    void (async () => {
      const [data, detected] = await Promise.all([loadFontData(), detectSelectedFont()]);
      if (!live) return;
      setFonts(data.allFonts);
      if (detected) setFromFont(detected);

      const { majorFont, minorFont } = data.themeFonts;
      const detectedLower = detected?.toLowerCase();
      const target = detectedLower && detectedLower === minorFont?.toLowerCase() ? majorFont : minorFont;
      if (target) setToFont((cur) => cur || target);
    })();
    return () => {
      live = false;
    };
  }, [isFont]);

  const title = isFont ? "Replace Font" : "Line Weight";
  const canApply = isFont ? fromFont.trim().length > 0 && toFont.trim().length > 0 : weight > 0;

  const apply = (): void => {
    if (isFont) onApplyFont(fromFont.trim(), toFont.trim(), scope === "all");
    else onApplyWeight(weight);
  };

  return (
    <fieldset className={styles.panel} aria-label={`${title} options`}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onCancel}>
          &times;
        </button>
      </div>

      {isFont ? (
        <>
          <div className={styles.row}>
            <label htmlFor="rf-from">From font</label>
            <input
              id="rf-from"
              type="text"
              list="rf-from-list"
              value={fromFont}
              disabled={busy}
              placeholder="Select source font"
              aria-label="From font"
              onChange={(e) => setFromFont(e.target.value)}
            />
            <datalist id="rf-from-list">
              {fonts.map((f) => (
                <option key={f.name} value={f.name} label={f.tag ? `${f.name} [${f.tag}]` : undefined} />
              ))}
            </datalist>
          </div>
          <div className={styles.row}>
            <label htmlFor="rf-to">To font</label>
            <input
              id="rf-to"
              type="text"
              list="rf-to-list"
              value={toFont}
              disabled={busy}
              placeholder="Select target font"
              aria-label="To font"
              onChange={(e) => setToFont(e.target.value)}
            />
            <datalist id="rf-to-list">
              {fonts.map((f) => (
                <option key={f.name} value={f.name} label={f.tag ? `${f.name} [${f.tag}]` : undefined} />
              ))}
            </datalist>
          </div>
          <div className={styles.row}>
            <label htmlFor="rf-scope">Scope</label>
            <select
              id="rf-scope"
              value={scope}
              disabled={busy}
              aria-label="Scope"
              onChange={(e) => setScope(e.target.value as "slide" | "all")}
            >
              <option value="slide">Current Slide</option>
              <option value="all">All Slides</option>
            </select>
          </div>
        </>
      ) : (
        <div className={styles.row}>
          <label htmlFor="cw-weight">Line weight (pt)</label>
          <input
            id="cw-weight"
            type="number"
            min={0.25}
            max={20}
            step={0.25}
            value={weight}
            disabled={busy}
            aria-label="Line weight"
            onChange={(e) => {
              const n = Number.parseFloat(e.target.value);
              if (!Number.isNaN(n)) setWeight(n);
            }}
          />
        </div>
      )}

      <div className={styles.footer}>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={styles.primary} onClick={apply} disabled={busy || !canApply}>
          {busy ? "Working…" : "OK"}
        </button>
      </div>
    </fieldset>
  );
}
